/* eslint-disable react/prop-types */
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  Grid,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useToast,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { XIcon } from "@heroicons/react/outline";
import { useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";

import { objectApiClient } from "../../apiConfig";
import CustomerLogo from "../../common/CustomerLogo";
import KeyBoardDrawer from "../../common/KeyBoardDrawer";
import MotionDiv from "../../common/MotionDiv";
import ScanNow from "../../common/ScanNow";
import LanguageSelect from "../../common/LanguageSelect";
import { _basePath } from "../../config";
import { UserContext } from "../../context";

import { useApiTenant, useApiUser } from "../../context/ApiAuthContext";

function Barcode() {
  const { tenantId } = useApiTenant();
  const [scanEnabled, setScanEnabled] = useState(false);
  const toast = useToast();
  const history = useHistory();
  const { customerName, tenantName } = useApiUser();
  const { setUserData } = React.useContext(UserContext);
  const formRef = React.useRef();
  const keyboardRef = React.useRef();
  const barcodeRef = React.useRef();

  useEffect(() => {
    barcodeRef.current.focus();
  }, []);

  const { t, i18n } = useTranslation();
  const showKeyboard =
    localStorage.getItem("keyboard") === "true" ||
    window?.navigator?.userAgentData?.mobile === false ||
    window?.navigator?.userAgentData?.platform === "Linux" ||
    window?.navigator?.userAgent?.includes("Windows") ||
    window?.navigator?.userAgent?.includes("armv7l");
  const toastError = (err) => {
    let title = t("error.error");
    let description = t("error.unknown_error");

    if (err?.response?.status === 400) {
      title = t("error.bad_request");
      description = t("error.please_try_again");
    }
    if (err?.response?.status === 401) {
      title = t("error.unauthorized");
      description = t("error.please_see_staff");
    }
    if (err?.response?.status === 403) {
      title = t("error.forbidden");
      description = t("error.please_see_staff");
    }
    if (err?.response?.status === 404) {
      title = t("error.not_found");
      description = t("error.please_try_again");
    }

    toast({
      title: title,
      description: description,
      status: "error",
      duration: 6000,
      isClosable: true,
      containerStyle: {
        transform: "scale(1.2)",
        transformOrigin: "bottom center",
      },
    });
  };
  const {
    register,
    formState: { isDirty },
    handleSubmit,
    getValues,
    setValue,
    reset,
  } = useForm({ defaultValues: { barcode: "" } });
  const { barcode } = getValues();
  const { isError, isLoading, mutate } = useMutation(
    (barcode) =>
      objectApiClient.barcodeGet(
        barcode,
        true,
        undefined,
        undefined,
        undefined,
        undefined,
        tenantId,
        "1.0",
        "en-US,en"
      ),
    {
      onSuccess: (res, variables) => {
        const resCount = res.data.count;
        if (resCount === 0) {
          toast({
            title: t("error.not_found"),
            description: t("error.please_try_again"),
            status: "error",
            duration: 4000,
            isClosable: true,
            containerStyle: {
              transform: "scale(1.2)",
              transformOrigin: "bottom center",
            },
          });
          startScanner();
          onClearInput();
        } else {
          const membersResult = res?.data?.data;
          if (resCount === 1) {
            setUserData({
              search: variables,
              user: membersResult[0],
              loggedIn: true,
            });
            history.push(`${_basePath}/welcome`);
          } else if (resCount > 1) {
            setUserData({
              users: membersResult,
              search: barcode,
              loggedIn: false,
            });
            history.push(`${_basePath}/select`);
          }
        }
      },
      onError: (err) => {
        toastError(err);
        onClearInput();
      },
    }
  );

  useEffect(() => {
    i18n.changeLanguage("en");
  }, [i18n]);

  useEffect(() => {
    window.kp_padlockScan_scanningDidFinish = (scanType, scanData) => {
      onSubmit({ barcode: scanData });
    };
    window.kp_padlockScan_scanningStateDidChange = (state) => {
      setScanEnabled(state === 1);
    };
    startScanner();
  }, []);

  function startScanner() {
    if (typeof kp_padlockScan_start === "function")
      // eslint-disable-next-line no-undef
      kp_padlockScan_start();
  }
  function onSubmit(data) {
    mutate(data.barcode);
  }
  function onKeyboardChange(v) {
    setValue("barcode", v.default, { shouldDirty: true });
  }
  function onClearInput() {
    reset();
    if (showKeyboard) keyboardRef.current.clearInput();
  }

  function handleFocus() {
    barcodeRef.current.click();
  }

  return (
    <>
      {scanEnabled && <ScanNow />}
      <LanguageSelect handleLanguageChange={handleFocus} />
      <Grid layerStyle={"kioskBarcodeLayout"} data-testid={"GRID"}>
        <Flex
          layerStyle={"kioskBarcodeLayout.Login"}
          justifyContent={"center"}
          alignItems={{ base: "start", sm: "center" }}
          h={"full"}
          w={"full"}
          flex={1}
          overflow={"hidden"}
        >
          <MotionDiv
            h={"full"}
            w={"full"}
            flex={1}
            justifyContent={"center"}
            alignItems={{ base: "start", sm: "center" }}
            display={"flex"}
            key={"form"}
            animate={{
              x: [420, 0],
              opacity: [0, 1],
            }}
            transition={{
              duration: 0.5,
              ease: "easeInOut",
            }}
          >
            <Box>
              <Text
                textTransform="uppercase"
                color="brand.text.DEFAULT"
                fontSize={{ base: "1xl", md: "2xl" }}
              >
                <Trans i18nKey="welcome_to_customer">Welcome to</Trans>
              </Text>
              <Text
                color="brand.text.DEFAULT"
                fontWeight="bold"
                fontSize={{ base: "4xl", md: "5xl" }}
                lineHeight={"1em"}
              >
                {customerName}
              </Text>
              <Text
                color="brand.text.DEFAULT"
                lineHeight={"md"}
                fontSize={{ base: "2xl", md: "4xl" }}
                mb={5}
              >
                {tenantName}
              </Text>

              <Text
                color="brand.text.DEFAULT"
                fontSize={{ base: "md", md: "lg" }}
                mb={2}
                noOfLines={4}
              >
                <Trans i18nKey={"scan_or_enter"}>
                  Scan or enter your ID below to begin
                </Trans>
              </Text>

              <form
                ref={formRef}
                onSubmit={handleSubmit(onSubmit)}
                data-testid="barcode-form"
              >
                <FormControl>
                  <InputGroup>
                    <Input
                      autoFocus
                      autoComplete="off"
                      onBlur={() => {
                        setTimeout(() => barcodeRef.current.focus(), 10);
                      }}
                      bg="white"
                      size={"lg"}
                      name="barcode"
                      ref={(el) => {
                        register(el, { required: true });
                        barcodeRef.current = el;
                      }}
                      type="text"
                      placeholder={t("id_barcode_placeholder", "ID / Barcode")}
                      data-testid={"id-barcode-input"}
                    />
                    {isDirty && (
                      <InputRightElement h={"100%"}>
                        <IconButton
                          aria-label="Clear Search"
                          onClick={onClearInput}
                          size="sm"
                          data-testid={"clear-barcode"}
                        >
                          <Icon as={XIcon} color="gray.300" fontSize="1.5em" />
                        </IconButton>
                      </InputRightElement>
                    )}
                  </InputGroup>
                </FormControl>
                <Box display="flex" mt={8} justifyContent={"end"} width="full">
                  <Button
                    size={"lg"}
                    variant="kioskPrimary"
                    type="submit"
                    isLoading={isLoading && !isError}
                    loadingText={t("loader.loading", "Loading...")}
                    disabled={!isDirty || !barcode || barcode?.length === 0}
                    data-testid={"id-barcode-submit-button"}
                  >
                    <Trans i18nKey={"actions.submit"}>Submit</Trans>
                  </Button>
                </Box>
              </form>
            </Box>
          </MotionDiv>
        </Flex>
        <Flex
          flex={0}
          alignItems={"center"}
          py={2}
          display={{ base: "none", sm: "flex" }}
        >
          <Divider
            orientation="vertical"
            h={"50%"}
            py={8}
            borderColor={"brand.primary.700"}
          />
        </Flex>
        <CustomerLogo />
      </Grid>
      {showKeyboard && (
        <KeyBoardDrawer
          keyboardRef={keyboardRef}
          onKeyboardChange={onKeyboardChange}
          onSubmit={handleSubmit(onSubmit)}
          data-testid={"keyboard-drawer"}
        />
      )}
    </>
  );
}

export default Barcode;
